import { c as _c } from "react/compiler-runtime";
import { IcoTrash16 } from '@onefootprint/icons';
import { Dialog, IconButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { type KeyboardEvent, type ReactNode, useId, useState } from 'react';
import trapFieldFocus from './trap-field-focus';
type BlockShellProps = {
  children: ReactNode;
  label: string;
  hint?: string;
  labelStart?: ReactNode;
  contentClassName?: string;
  deleteLabel?: string;
  title?: string;
  editable?: boolean;
  onExit?: () => void;
  // Bundled so they cannot be passed apart: a delete button with nothing to confirm does nothing.
  onDelete?: {
    confirmation: {
      title: string;
      description: string;
    };
    onConfirm: () => void;
  };
};
const BlockShell = t0 => {
  const $ = _c(37);
  const {
    children,
    label,
    hint,
    title,
    editable: t1,
    onDelete,
    onExit,
    labelStart,
    contentClassName,
    deleteLabel
  } = t0;
  const editable = t1 === undefined ? false : t1;
  const [confirming, setConfirming] = useState(false);
  const keyboardHintId = useId();
  let t2;
  if ($[0] !== editable || $[1] !== onExit) {
    t2 = event => {
      if (editable && onExit) {
        trapFieldFocus(event, onExit);
      }
    };
    $[0] = editable;
    $[1] = onExit;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleKeyDown = t2;
  let t3;
  if ($[3] !== label || $[4] !== labelStart) {
    t3 = labelStart ?? <p className="text-label-3 text-tertiary">{label}</p>;
    $[3] = label;
    $[4] = labelStart;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== hint) {
    t4 = hint && <p className="text-caption-2 text-tertiary">{hint}</p>;
    $[6] = hint;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== deleteLabel || $[9] !== editable || $[10] !== label || $[11] !== onDelete) {
    t5 = editable && onDelete && <IconButton aria-label={deleteLabel ?? `Delete ${label.toLowerCase()}`} className="ml-auto" color="tertiary" icon={IcoTrash16} onClick={() => setConfirming(true)} size="tiny" />;
    $[8] = deleteLabel;
    $[9] = editable;
    $[10] = label;
    $[11] = onDelete;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t5) {
    t6 = <div className="mb-1.5 flex h-6 items-center gap-2" data-block-header={true}>{t3}{t4}{t5}</div>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const t7 = title || label;
  const t8 = editable && onExit ? keyboardHintId : undefined;
  let t9;
  if ($[17] !== contentClassName) {
    t9 = cx("rounded-default border border-tertiary bg-primary px-5.5 py-5", contentClassName);
    $[17] = contentClassName;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== editable || $[20] !== keyboardHintId || $[21] !== onExit) {
    t10 = editable && onExit && <p className="sr-only" id={keyboardHintId}>Tab moves between the title and items in this block. Press Escape to return to the document.</p>;
    $[19] = editable;
    $[20] = keyboardHintId;
    $[21] = onExit;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== children || $[24] !== t10 || $[25] !== t7 || $[26] !== t8 || $[27] !== t9) {
    t11 = <section aria-label={t7} aria-describedby={t8} className={t9}>{t10}{children}</section>;
    $[23] = children;
    $[24] = t10;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== confirming || $[30] !== onDelete) {
    t12 = onDelete && <Dialog isConfirmation={true} onClose={() => setConfirming(false)} open={confirming} primaryButton={{
      label: "Yes, delete",
      onClick: () => {
        onDelete.onConfirm();
        setConfirming(false);
      },
      variant: "destructive"
    }} secondaryButton={{
      label: "No",
      onClick: () => setConfirming(false)
    }} size="compact" title={onDelete.confirmation.title}><p className="text-center text-body-2 text-secondary">{onDelete.confirmation.description}</p></Dialog>;
    $[29] = confirming;
    $[30] = onDelete;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== handleKeyDown || $[33] !== t11 || $[34] !== t12 || $[35] !== t6) {
    t13 = <div className="group relative" onKeyDownCapture={handleKeyDown}>{t6}{t11}{t12}</div>;
    $[32] = handleKeyDown;
    $[33] = t11;
    $[34] = t12;
    $[35] = t6;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
};
export default BlockShell;